import { useEffect, useMemo, useState } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { RunMap } from '$lib/RunMap';
import { SummaryPanel } from '$lib/SummaryPanel';
import { DetailPanel } from '$lib/DetailPanel';
import { RunList } from '$lib/RunList';
import { Drawer } from '$lib/Drawer';
import { PhotoLightbox } from '$lib/PhotoLightbox';
import { PhotoStack } from '$lib/PhotoStack';
import { Flyby } from '$lib/Flyby';
import { LoadingScreen, LOADER_MIN_MS, LOADER_FADE_MS } from '$lib/LoadingScreen';
import { loadPhotos, loadRunDetail, loadRunsIndex } from '$lib/data';
import type { Photo, RunDetail, RunsIndex } from '$lib/types';

const PATH_PREFIX = '/run/';

function readRunIdFromPath(): string | null {
  const p = window.location.pathname;
  const i = p.indexOf(PATH_PREFIX);
  if (i === -1) return null;
  const id = decodeURIComponent(p.slice(i + PATH_PREFIX.length).replace(/\/+$/, ''));
  return id || null;
}

function basePath(): string {
  const p = window.location.pathname;
  const i = p.indexOf(PATH_PREFIX);
  return i === -1 ? p : p.slice(0, i) || '/';
}

export default function App() {
  const [index, setIndex] = useState<RunsIndex | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(() => readRunIdFromPath());
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [stripLightboxIndex, setStripLightboxIndex] = useState<number | null>(null);
  const [showAllPhotoPins, setShowAllPhotoPins] = useState(false);
  const [mapInstance, setMapInstance] = useState<MapboxMap | null>(null);
  const [flybyRunId, setFlybyRunId] = useState<string | null>(null);
  const [flybyDetail, setFlybyDetail] = useState<RunDetail | null>(null);
  const [flybyLoading, setFlybyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minDelayPassed, setMinDelayPassed] = useState(false);
  // Loader lifecycle: 'show' → 'fading' (crossfade with app) → 'gone' (unmounted).
  const [loaderPhase, setLoaderPhase] = useState<'show' | 'fading' | 'gone'>('show');

  useEffect(() => {
    loadRunsIndex()
      .then(setIndex)
      .catch((err: Error) => setError(err.message));
    loadPhotos()
      .then(setPhotos)
      .catch((err: Error) => console.warn('photos unavailable:', err.message));
    const t = window.setTimeout(() => setMinDelayPassed(true), LOADER_MIN_MS);
    return () => window.clearTimeout(t);
  }, []);

  // Sync selectedId → URL path (and back/forward navigation → selectedId).
  useEffect(() => {
    const base = basePath();
    const target = selectedId
      ? `${base === '/' ? '' : base}${PATH_PREFIX}${encodeURIComponent(selectedId)}`
      : base;
    const current = `${window.location.pathname}`;
    if (current !== target) {
      window.history.pushState(null, '', `${target}${window.location.search}${window.location.hash}`);
    }
  }, [selectedId]);

  useEffect(() => {
    const onPopState = () => setSelectedId(readRunIdFromPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Once runs load, drop a stale id from the URL if it doesn't match anything.
  useEffect(() => {
    if (!index || !selectedId) return;
    if (!index.runs.some((r) => r.id === selectedId)) setSelectedId(null);
  }, [index, selectedId]);

  const dataReady = !!index || !!error;
  useEffect(() => {
    if (loaderPhase !== 'show') return;
    if (!dataReady || !minDelayPassed) return;
    setLoaderPhase('fading');
    const t = window.setTimeout(() => setLoaderPhase('gone'), LOADER_FADE_MS);
    return () => window.clearTimeout(t);
  }, [dataReady, minDelayPassed, loaderPhase]);

  const selectedRun = useMemo(
    () => (index && selectedId ? index.runs.find((r) => r.id === selectedId) ?? null : null),
    [index, selectedId]
  );

  const flybyRun = useMemo(
    () => (index && flybyRunId ? index.runs.find((r) => r.id === flybyRunId) ?? null : null),
    [index, flybyRunId]
  );

  // Load detail for flyby (cached after first load).
  useEffect(() => {
    if (!flybyRunId) {
      setFlybyDetail(null);
      setFlybyLoading(false);
      return;
    }
    let cancelled = false;
    setFlybyLoading(true);
    loadRunDetail(flybyRunId)
      .then((d) => {
        if (!cancelled) setFlybyDetail(d);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setFlybyRunId(null);
      })
      .finally(() => {
        if (!cancelled) setFlybyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flybyRunId]);

  // If the user deselects the run while a flyby is queued, cancel it.
  useEffect(() => {
    if (flybyRunId && !selectedId) setFlybyRunId(null);
  }, [flybyRunId, selectedId]);

  const visiblePhotos = useMemo(
    () => (selectedRun ? photos.filter((p) => p.runId === selectedRun.id) : photos),
    [photos, selectedRun]
  );

  // Reset the strip lightbox when switching runs so the open index doesn't
  // point into a different photo set.
  useEffect(() => {
    setStripLightboxIndex(null);
  }, [selectedRun?.id]);

  const hasMapboxToken = !!import.meta.env.VITE_MAPBOX_TOKEN;

  if (!hasMapboxToken) return <TokenMissing />;
  if (error && loaderPhase === 'gone') return <Fatal message={error} />;
  if (!index) {
    return (
      <>
        {loaderPhase !== 'gone' && <LoadingScreen fadingOut={loaderPhase === 'fading'} />}
      </>
    );
  }
  if (index.runs.length === 0) return <EmptyState />;

  const drawerKey = selectedRun ? `detail:${selectedRun.id}` : 'list';
  const flybyActive = !!flybyRun;
  const flybyReady = flybyActive && !!flybyDetail && !!mapInstance;

  return (
    <div className="relative w-full h-full overflow-hidden">
      <RunMap
        runs={index.runs}
        photos={photos}
        selectedId={selectedId}
        externalHoverId={selectedId ? null : hoveredCardId}
        showAllPhotos={showAllPhotoPins}
        flybyActive={flybyActive}
        onSelect={setSelectedId}
        onMapReady={setMapInstance}
      />
      {!flybyActive && (
        <>
          <SummaryPanel
            index={index}
            photos={visiblePhotos}
            onOpenPhoto={setStripLightboxIndex}
            showPhotoPins={showAllPhotoPins}
            onTogglePhotoPins={
              !selectedRun && photos.length > 0
                ? () => setShowAllPhotoPins((v) => !v)
                : undefined
            }
          />
          <Drawer viewKey={drawerKey} mobileSize={selectedRun ? 'tall' : 'compact'}>
            {selectedRun ? (
              <DetailPanel
                run={selectedRun}
                photos={photos}
                onClose={() => setSelectedId(null)}
                onPlayFlyby={() => setFlybyRunId(selectedRun.id)}
              />
            ) : (
              <RunList
                runs={index.runs}
                totals={index.totals}
                hoveredId={hoveredCardId}
                onHover={setHoveredCardId}
                onSelect={setSelectedId}
                onPlay={(id) => {
                  setSelectedId(id);
                  setFlybyRunId(id);
                }}
              />
            )}
          </Drawer>
          <PhotoStack photos={visiblePhotos} onOpen={setStripLightboxIndex} />
          {stripLightboxIndex !== null && visiblePhotos[stripLightboxIndex] && (
            <PhotoLightbox
              photos={visiblePhotos}
              index={stripLightboxIndex}
              onChange={setStripLightboxIndex}
              onClose={() => setStripLightboxIndex(null)}
            />
          )}
        </>
      )}
      {flybyActive && flybyLoading && !flybyDetail && (
        <div className="absolute inset-x-0 top-4 flex justify-center pointer-events-none z-50">
          <div className="px-3 py-1.5 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-md">
            Preparing flyby…
          </div>
        </div>
      )}
      {flybyReady && flybyRun && flybyDetail && mapInstance && (
        <Flyby
          map={mapInstance}
          run={flybyRun}
          detail={flybyDetail}
          photos={photos}
          onClose={() => setFlybyRunId(null)}
        />
      )}
      {loaderPhase !== 'gone' && <LoadingScreen fadingOut={loaderPhase === 'fading'} />}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h2 className="font-display text-3xl text-ink-900 mb-3">No runs yet</h2>
        <p className="text-ink-500">
          Drop GPX files into <code className="font-mono text-ink-700">data/</code> and run{' '}
          <code className="font-mono text-ink-700">pnpm build:data</code>.
        </p>
      </div>
    </div>
  );
}

function TokenMissing() {
  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h2 className="font-display text-3xl text-ink-900 mb-3">Mapbox token missing</h2>
        <p className="text-ink-500 mb-4">
          Create a <code className="font-mono text-ink-700">.env.local</code> file with{' '}
          <code className="font-mono text-ink-700">VITE_MAPBOX_TOKEN=your_token</code>.
        </p>
        <a
          href="https://account.mapbox.com/access-tokens/"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-accent hover:underline text-sm font-medium"
        >
          Get a token on Mapbox →
        </a>
      </div>
    </div>
  );
}

function Fatal({ message }: { message: string }) {
  return (
    <div className="w-full h-full flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h2 className="font-display text-3xl text-ink-900 mb-3">Something went wrong</h2>
        <p className="text-ink-500 font-mono text-sm">{message}</p>
      </div>
    </div>
  );
}
